import { useContentPageLink } from "@/react/components/SettingsApp/Posts/hooks/useContentPageLink";
import { usePlanStatus } from "@/react/hooks/usePlanStatus";
import { usePunditUserContext } from "@circle-react/contexts";
import { useMasqueradeContext } from "@circle-react/contexts/masqueradeContext";
import { useThemeContext } from "@circle-react/providers";
import { useAdminOptions } from "./adminOptions";
import { getContent } from "./getContent";
import { moderatorOptions } from "./moderatorOptions";
import type { DropdownOption } from "./types";
import { useGetModeration } from "./useGetModeration";
import { useGetShortcutsModal } from "./useGetShortcutsModal";
import { useGetToggleTheme } from "./useGetToggleTheme";
import { useGetviewAs } from "./useGetViewAs";

export interface UseDropdownOptionsProps {
  isAdmin: boolean;
  isModerator: boolean;
  isWorkflowsEnabled: boolean;
  memberCount?: number;
  moderationCount?: number;
  showCustomizeTheme: boolean;
  showToggleTheme: boolean;
}

export const useDropdownOptions = ({
  isAdmin,
  isModerator,
  memberCount,
  moderationCount,
  showToggleTheme,
  showCustomizeTheme,
  isWorkflowsEnabled,
}: UseDropdownOptionsProps): DropdownOption[] => {
  const { currentAppearance, toggleAppearance } = useThemeContext();
  const shortcutsModalButton = useGetShortcutsModal();
  const toggleTheme = useGetToggleTheme({
    currentAppearance,
    toggleAppearance,
    showToggleTheme,
  });

  const { isBannerVisible, showBanner } = useMasqueradeContext();
  const viewAs = useGetviewAs({ showBanner });

  const moderation = useGetModeration(moderationCount);
  const contentPageLink = useContentPageLink();
  const content = getContent(contentPageLink);

  const { isCommunityOnPlus } = usePlanStatus();
  const { currentCommunity, currentCommunitySettings } = usePunditUserContext();

  const isAffiliatesEnabled = Boolean(
    currentCommunity?.affiliates_feature_flag_enabled,
  );

  const hasAIAgentsAccess = Boolean(
    currentCommunity?.ai_agents_feature_flag_enabled,
  );

  const isAdminDashboardEnabled = Boolean(
    currentCommunitySettings?.growth_admin_dashboard_enabled,
  );

  const adminOptions = useAdminOptions({
    memberCount,
    showCustomizeTheme,
    toggleTheme,
    viewAs,
    content,
    isCommunityOnPlus,
    isWorkflowsEnabled,
    isAffiliatesEnabled,
    isBannerVisible,
    shortcutsModalButton,
    hasAIAgentsAccess,
    isAdminDashboardEnabled,
  });

  if (isAdmin) {
    return adminOptions;
  }

  if (isModerator) {
    return moderatorOptions({
      moderation,
      showToggleTheme,
      toggleTheme,
      content,
      shortcutsModalButton,
    });
  }

  return [
    ...(shortcutsModalButton ? [shortcutsModalButton] : []),
    ...toggleTheme,
  ];
};
